import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/fr";
import { authFetch } from "../services/authAPI";
import Field from "../components/Field";
import Select from "../components/Select";
import Modal from "../components/Modal";
import Checkbox from "../components/Checkbox";

moment.locale("fr");

const ROLE_OPTIONS = [
    { value: "logistics", label: "Logistique" },
    { value: "routes", label: "Route" },
    { value: "users:manage", label: "Gestion des utilisateurs" },
    { value: "billing:read", label: "Consultation des factures" },
    { value: "credentials:manage", label: "Gestion des identifiants" },
];

const ROLE_LABELS = {
    ROLE_MANAGER: "Manager",
    ROLE_EMPLOYEE: "Employé"
};

const ROLE_CLASSES = {
    ROLE_MANAGER: "success",
    ROLE_EMPLOYEE: "secondary"
};

export default function UsersPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 5;

    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [error, setError] = useState(null);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteFirstName, setInviteFirstName] = useState("");
    const [inviteLastName, setInviteLastName] = useState("");
    const [invitePermissions, setInvitePermissions] = useState([]);

    const [inviteViolations, setInviteViolations] = useState([]);

    const [showDeleteInvitationModal, setShowDeleteInvitationModal] = useState(false);
    const [deletingInvitation, setDeletingInvitation] = useState(null);

    async function fetchUsers() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users`);
            if (!response.ok) {
                setError("Erreur lors du chargement des utilisateurs.");
                return;
            }
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError("Erreur lors du chargement des utilisateurs.");
        }
    }

    async function fetchInvitations() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/invitations`);
            if (!response.ok) return;
            const data = await response.json();
            setInvitations(data);
        } catch (err) {
            // Les invitations sont secondaires, pas de message d'erreur bloquant
        }
    }

    async function handleCreateInvitation() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/invitations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: inviteEmail,
                    firstName: inviteFirstName,
                    lastName: inviteLastName,
                    permissions: invitePermissions,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setInviteViolations(errorData.violations ?? [{ message: "Erreur lors de l'envoi de l'invitation." }]);
                return;
            }
            setShowInviteModal(false);
            setInviteViolations([]);
            setInviteEmail("");
            setInviteFirstName("");
            setInviteLastName("");
            setInvitePermissions([]);
            fetchInvitations();
        } catch (err) {
            setInviteViolations([{ message: "Erreur lors de l'envoi de l'invitation." }]);
        }
    }

    async function handleDeleteInvitation() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/invitations/${deletingInvitation.id}`, {
                method: "DELETE",
            });
            if (response.ok) {
                setShowDeleteInvitationModal(false);
                setDeletingInvitation(null);
                fetchInvitations();
            } else {
                setError("Erreur lors de l'annulation de l'invitation.");
            }
        } catch (err) {
            setError("Erreur lors de l'annulation de l'invitation.");
        }
    }

    useEffect(() => {
        fetchUsers();
        fetchInvitations();
    }, []);

    const filteredUsers = users.filter(
        (user) =>
            user.firstName?.toLowerCase().includes(search.toLowerCase()) ||
            user.lastName?.toLowerCase().includes(search.toLowerCase()) ||
            user.email?.toLowerCase().includes(search.toLowerCase()),
    );

    const totalPages = Math.ceil(filteredUsers.length / entriesPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * entriesPerPage,
        currentPage * entriesPerPage,
    );

    return (
        <>
            <Modal
                title="Annuler l'invitation"
                body={
                    <p>
                        Êtes-vous sûr de vouloir annuler l'invitation de{" "}
                        <strong>{deletingInvitation?.firstName} {deletingInvitation?.lastName}</strong> ({deletingInvitation?.email}) ?
                    </p>
                }
                button={{ close: "Annuler", valid: "Confirmer" }}
                showModal={showDeleteInvitationModal}
                setShowModal={setShowDeleteInvitationModal}
                onSubmit={handleDeleteInvitation}
            />
            <Modal
                title="Inviter un utilisateur"
                body={
                    <>
                        {inviteViolations.length > 0 && (
                            <div className="alert alert-danger d-flex align-items-center" role="alert">
                                <i className="fas fa-times-circle me-3"></i>
                                <ul className="mb-0 list-unstyled">
                                    {inviteViolations.map((v, i) => (
                                        <li key={i}>{v.message}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Field
                            name="inviteFirstName"
                            label="Prénom"
                            value={inviteFirstName}
                            onChange={(e) => setInviteFirstName(e.target.value)}
                            type="text"
                        />
                        <Field
                            name="inviteLastName"
                            label="Nom"
                            value={inviteLastName}
                            onChange={(e) => setInviteLastName(e.target.value)}
                            type="text"
                        />
                        <Field
                            name="inviteEmail"
                            label="Email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            type="email"
                        />
                        <hr />
                        <h6>Permissions</h6>
                        {ROLE_OPTIONS.map((option) => (
                            <Checkbox
                                key={option.value}
                                name={option.value}
                                value={option.value}
                                label={option.label}
                                checked={invitePermissions.includes(option.value)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setInvitePermissions([...invitePermissions, option.value]);
                                    } else {
                                        setInvitePermissions(invitePermissions.filter((p) => p !== option.value));
                                    }
                                }}
                            />
                        ))}
                    </>
                }
                button={{ close: "Annuler", valid: "Inviter" }}
                showModal={showInviteModal}
                setShowModal={setShowInviteModal}
                onSubmit={handleCreateInvitation}
            />
            <section>
                <div className="card mb-4">
                    <div className="card-header py-3">
                        <div className="row py-3">
                            <div className="col-sm-6 text-center text-sm-start">
                                <h5 className="mb-4 mb-sm-0 mt-2">
                                    Gestion des utilisateurs
                                </h5>
                            </div>
                            <div className="col-sm-6 text-center text-sm-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary me-2"
                                    onClick={() => { setShowInviteModal(true); setInviteViolations([]); }}
                                >
                                    Inviter
                                    <i className="fas fa-envelope ms-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body py-5 px-5">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <div className="d-flex align-items-center mb-4">
                            <div className="flex-grow-1">
                                <Field
                                    name="search"
                                    label="Rechercher"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    type="text"
                                    margin={false}
                                />
                            </div>
                        </div>
                        <table className="table table-hover card-body align-middle mb-0 bg-white">
                            <thead className="bg-light">
                                <tr>
                                    <th className="text-left">Utilisateur</th>
                                    <th className="text-center">Email</th>
                                    <th className="text-center">Rôle</th>
                                    <th className="text-center">Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedUsers.map((user) => (
                                    <tr key={user.id} onClick={() => navigate(`/users/${user.id}`)} style={{ cursor: "pointer" }}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div>
                                                    <p className="fw-bold mb-1">
                                                        {user.firstName} {user.lastName}
                                                    </p>
                                                    <p className="text-muted mb-0">
                                                        {user.createdAt
                                                            ? `Ajouté le ${moment(user.createdAt).format("dddd D MMMM [à] HH:mm")}`
                                                            : "—"}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <p className="fw-normal mb-0">{user.email}</p>
                                        </td>
                                        <td className="text-center">
                                            <span className={"badge badge-" + ROLE_CLASSES[user.role]}>
                                                {ROLE_LABELS[user.role]}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {user.deletedAt ? (
                                                <i className="fa-solid fa-circle-xmark text-danger"></i>
                                            ) : (
                                                <i className="fa-solid fa-circle-check text-success"></i>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <nav className="d-flex justify-content-end mt-4">
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>
                                            <i className="fas fa-chevron-left fa-xs"></i>
                                        </button>
                                    </li>
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                                                {i + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>
                                            <i className="fas fa-chevron-right fa-xs"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                </div>
            </section>

            {invitations.length > 0 && (
                <section>
                    <div className="card mb-4">
                        <div className="card-header py-3">
                            <div className="row py-3 align-items-center">
                                <div className="col-sm-6 text-center text-sm-start">
                                    <h5 className="mb-4 mb-sm-0 mt-2">Invitations en attente</h5>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-5 px-5">
                            <table className="table table-hover align-middle mb-0 bg-white">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="text-left">Nom</th>
                                        <th className="text-center">Email</th>
                                        <th className="text-center">Rôle</th>
                                        <th className="text-center">Envoyée le</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invitations.map((invitation) => (
                                        <tr key={invitation.id}>
                                            <td>
                                                <p className="fw-bold mb-0">
                                                    {invitation.firstName} {invitation.lastName}
                                                </p>
                                            </td>
                                            <td className="text-center">{invitation.email}</td>
                                            <td className="text-center">
                                                <span className={"badge badge-" + (ROLE_CLASSES[invitation.role] ?? "secondary")}>
                                                    {ROLE_LABELS[invitation.role] ?? invitation.role}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                {invitation.createdAt
                                                    ? moment(invitation.createdAt).format("dddd D MMMM YYYY [à] HH:mm")
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm btn-rounded text-danger"
                                                    onClick={() => {
                                                        setDeletingInvitation(invitation);
                                                        setShowDeleteInvitationModal(true);
                                                    }}
                                                >
                                                    Annuler
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}
